import { X } from 'lucide-react'

import { cn } from '@/utils/ui/styles'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import { useTableSearch } from '../hooks/use-table-search'

type TableSearchProps = {
	placeholder?: string
	paramName?: string
	debounceMs?: number
	/** Fewest non-space characters that count as a search (default: 0). */
	minLength?: number
	analyticsEvent?: string
	className?: string
}

/**
 * Search bar component with URL sync and debounce
 *
 * Features:
 * - Syncs search query with URL params
 * - Debounced input to prevent excessive URL updates; Enter and clear apply at once
 * - Optional PostHog analytics tracking
 * - Clear button when query is present
 *
 * @example
 * <TableSearch
 *   placeholder="Search for Integration, Name or Description"
 *   analyticsEvent="playbooks_search"
 * />
 */
export function TableSearch({
	placeholder = 'Search for "Name" or "Description"',
	paramName,
	debounceMs,
	minLength,
	analyticsEvent,
	className,
}: TableSearchProps) {
	const { query, setQuery, submit } = useTableSearch({
		paramName,
		debounceMs,
		minLength,
		analyticsEvent,
	})

	return (
		<div
			className={cn(
				'relative flex w-full flex-row items-center justify-start gap-4',
				className,
			)}
		>
			<Input
				type="text"
				name="search"
				onChange={(event) => setQuery(event.target.value)}
				onKeyDown={(event) => {
					if (event.key === 'Enter') submit()
				}}
				value={query}
				placeholder={placeholder}
				className="h-12 max-w-full"
				tabIndex={0}
			/>
			{query && query.length > 0 && (
				<Button
					size="icon"
					variant="ghost"
					title="Clear search"
					name="clear_search"
					onClick={() => submit('')}
					className="absolute inset-y-2 right-2"
				>
					<X />
				</Button>
			)}
		</div>
	)
}
